import lockScroll from '../../utils/lockScroll';

const popupCookie = 'beauty_formulas_newsletter_popup_seen';

const signupStorageKey = 'beauty_formulas_newsletter_popup_subscribed';
const dismissedStorageKey = 'beauty_formulas_newsletter_popup_dismissed';

export default function initNewsletterPopup() {
    const overlay = document.querySelector<HTMLElement>('[data-newsletter-popup]');
    const dialog = overlay?.querySelector<HTMLElement>('[role="dialog"]');
    const closeButton = overlay?.querySelector<HTMLButtonElement>('[data-newsletter-popup-close]');

    if (!overlay || !dialog || !closeButton) return;
    const readState = (key: string) => {
        try { return localStorage.getItem(key) === '1'; } catch { return false; }
    };
    const saveState = (key: string) => {
        try { localStorage.setItem(key, '1'); } catch { /* Cookie fallback below. */ }
    };
    const hasCookie = (value: string) => document.cookie.split('; ').includes(`${popupCookie}=${value}`);
    let popupSignupSuccess = readState(signupStorageKey) || hasCookie('subscribed');
    if (popupSignupSuccess) {
        overlay.remove();
        createCTAForm();
        return;
    }

    const popup = overlay;
    const dismissButton = closeButton;

    const offerButton = document.createElement('button');
    offerButton.type = 'button';
    offerButton.className = 'newsletter-popup-offer';
    offerButton.textContent = 'Newsletter offer';
    offerButton.setAttribute('aria-haspopup', 'dialog');
    offerButton.hidden = true;
    document.body.appendChild(offerButton);
    let previousFocus: HTMLElement | null = null;

    function open() {
        if (popupSignupSuccess) return;
        stopAutoOpen();
        previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        offerButton.hidden = true;
        popup.hidden = false;
        lockScroll(true);
        dismissButton.focus({ preventScroll: true });
        document.addEventListener('keydown', onKeydown);
    }

    function close() {
        popup.hidden = true;
        lockScroll(false);
        document.removeEventListener('keydown', onKeydown);
        if (popupSignupSuccess) {
            offerButton.remove();
            popup.remove();
        } else {
            saveState(dismissedStorageKey);
            document.cookie = `${popupCookie}=dismissed; Max-Age=31536000; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
            offerButton.hidden = false;
        }
        if (previousFocus?.isConnected && previousFocus !== document.body) {
            previousFocus.focus({ preventScroll: true });
        } else if (!popupSignupSuccess) {
            offerButton.focus({ preventScroll: true });
        }
    }

    function onOverlayClick(event: MouseEvent) {
        if (event.target === popup) close();
    }

    function onKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            close();
            return;
        }

        if (event.key !== 'Tab') return;
        const focusable = Array.from(
            dialog!.querySelectorAll<HTMLElement>(
                'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
        ).filter((element) => element.getClientRects().length > 0);
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    popup.addEventListener('click', onOverlayClick);
    dismissButton.addEventListener('click', close);
    offerButton.addEventListener('click', open);
    const gateway = document.querySelector<HTMLDialogElement>('[data-home-gateway]');
    const isTradeEnquiry = document.body.classList.contains('page-template-page-trade-enquiry')
        || location.pathname.replace(/\/+$/, '').endsWith('/trade-enquiry');

    function stopAutoOpen() {
        window.removeEventListener('scroll', onScroll);
        gateway?.removeEventListener('close', onScroll);
    }

    function onScroll() {
        if (window.scrollY < 1000 || gateway?.open) return;
        open();
    }

    // Visiting trade enquiry counts as dismissal, including on later pages.
    if (isTradeEnquiry) {
        saveState(dismissedStorageKey);
        document.cookie = `${popupCookie}=dismissed; Max-Age=31536000; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
    }

    if (readState(dismissedStorageKey) || hasCookie('dismissed') || hasCookie('1')) {
        offerButton.hidden = false;
    } else {
        window.addEventListener('scroll', onScroll, { passive: true });
        gateway?.addEventListener('close', onScroll);
        onScroll();
    }

    const successMessage = popup.querySelector('#success-message');

    if (!successMessage) return;

    const observer = new MutationObserver(() => {
        const visible = successMessage.getClientRects().length > 0;

        if (visible) {
            observer.disconnect();
            popupSignupSuccess = true;
            saveState(signupStorageKey);
            document.cookie = `${popupCookie}=subscribed; Max-Age=31536000; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
            offerButton.remove();

            const introduction = popup.querySelector<HTMLElement>(
                '#newsletter-popup-description'
            );
            if (introduction) introduction.hidden = true;
            dialog?.removeAttribute('aria-describedby');

            const formContainer = popup.querySelector('.newsletter-popup__form');
            if (!formContainer) return;

            const confirmation = document.createElement('div');
            confirmation.className = 'newsletter-popup__description';
            confirmation.setAttribute('role', 'status');
            confirmation.tabIndex = -1;

            for (const text of [
                'We sent you a confirmation email. You will be subscribed after clicking on confirm.',
                "If you don't receive an email, check your spam box.",
            ]) {
                const paragraph = document.createElement('p');
                paragraph.textContent = text;
                confirmation.appendChild(paragraph);
            }

            formContainer.replaceChildren(confirmation);
            confirmation.focus({ preventScroll: true });
        }
    });

    observer.observe(successMessage, {
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden'],
    });
}

export function createCTAForm() {
    const container = document.querySelector(
        '.email-signup__form.email-signup__brevo'
    ) as HTMLDivElement | null;
    if (!container) return;

    container.style.display = '';

    const template = document.createElement('template');
    template.innerHTML = `
<div class="sib-form">
    <div id="sib-form-container" class="sib-form-container">
        <div id="error-message" class="sib-form-message-panel">
            <div class="sib-form-message-panel__text sib-form-message-panel__text--center">
                <svg viewBox="0 0 512 512" class="sib-icon sib-notification__icon" aria-hidden="true">
                    <path d="M256 40c118.621 0 216 96.075 216 216 0 119.291-96.61 216-216 216-119.244 0-216-96.562-216-216 0-119.203 96.602-216 216-216m0-32C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm-11.49 120h22.979c6.823 0 12.274 5.682 11.99 12.5l-7 168c-.268 6.428-5.556 11.5-11.99 11.5h-8.979c-6.433 0-11.722-5.073-11.99-11.5l-7-168c-.283-6.818 5.167-12.5 11.99-12.5zM256 340c-15.464 0-28 12.536-28 28s12.536 28 28 28 28-12.536 28-28-12.536-28-28-28z" />
                </svg>
                <span class="sib-form-message-panel__inner-text">Your subscription could not be saved. Please try again.</span>
            </div>
        </div>

        <div id="success-message" class="sib-form-message-panel">
            <div class="sib-form-message-panel__text sib-form-message-panel__text--center">
                <svg viewBox="0 0 512 512" class="sib-icon sib-notification__icon" aria-hidden="true">
                    <path d="M256 8C119.033 8 8 119.033 8 256s111.033 248 248 248 248-111.033 248-248S392.967 8 256 8zm0 464c-118.664 0-216-96.055-216-216 0-118.663 96.055-216 216-216 118.664 0 216 96.055 216 216 0 118.663-96.055 216-216 216zm141.63-274.961L217.15 376.071c-4.705 4.667-12.303 4.637-16.97-.068l-85.878-86.572c-4.667-4.705-4.637-12.303.068-16.97l8.52-8.451c4.705-4.667 12.303-4.637 16.97.068l68.976 69.533 163.441-162.13c4.705-4.667 12.303-4.637 16.97.068l8.451 8.52c4.668 4.705 4.637 12.303-.068 16.97z" />
                </svg>
                <span class="sib-form-message-panel__inner-text">Your subscription has been successful.</span>
            </div>
        </div>

        <div id="sib-container" class="sib-container--large sib-container--vertical">
            <form id="sib-form" method="POST" action="https://604eabf0.sibforms.com/serve/MUIFAAIMOS2xLEsGNHLd-w3z66Y0fADK6mqwUWb9AQYjUAMFv6LvlYewrUe7JXPviydubMegOyRI9XQeeCy_ayd-ck64Gpbobjcm_kQUHIPrQ4sZ2Jb9i60WOVWCYdrftRzJ_q8TODPHKbhIZdglKEl19npJ_Lacqh4hHPtkjR9zQPaaJp0M8iUaPeZn3NA9DbQbmVKGLOY4YPpfBg==" data-type="subscription">
                <div class="sib-input sib-form-block">
                    <div class="form__entry entry_block">
                        <div class="form__label-row">
                            <label class="entry__label" for="EMAIL" data-required="*">Enter your email address to subscribe</label>
                            <div class="entry__field">
                                <input class="input" type="text" id="EMAIL" name="EMAIL" autocomplete="off" value="" placeholder="Email address" data-required="true" required>
                            </div>
                        </div>

                        <label class="entry__error entry__error--primary"></label>
                        <label class="entry__specification">Provide your email address to subscribe. For e.g abc@xyz.com</label>
                    </div>
                </div>

                <div class="sib-form-block email-signup__submit-wrap">
                    <button class="sib-form-block__button sib-form-block__button-with-loader" form="sib-form" type="submit">
                        <svg class="icon clickable__icon progress-indicator__icon sib-hide-loader-icon" viewBox="0 0 512 512" aria-hidden="true">
                            <path d="M460.116 373.846l-20.823-12.022c-5.541-3.199-7.54-10.159-4.663-15.874 30.137-59.886 28.343-131.652-5.386-189.946-33.641-58.394-94.896-95.833-161.827-99.676C261.028 55.961 256 50.751 256 44.352V20.309c0-6.904 5.808-12.337 12.703-11.982 83.556 4.306 160.163 50.864 202.11 123.677 42.063 72.696 44.079 162.316 6.031 236.832-3.14 6.148-10.75 8.461-16.728 5.01z" />
                        </svg>
                        <span>Sign me up</span>
                    </button>
                </div>

                <input type="text" name="email_address_check" value="" class="input--hidden">
                <input type="hidden" name="locale" value="en">
            </form>
        </div>
    </div>
</div>
`;

    const signUpform = template.content.firstElementChild;
    if (signUpform) container.appendChild(signUpform);
}
